if (!customElements.get('product-review-excerpts')) {
  class ProductReviewExcerpts extends HTMLElement {
    constructor() {
      super();
      this.maxReviews = 2;
      this.maxExcerptLength = 150;
      this.minExcerptLength = 18;
      this.rendered = false;
    }

    connectedCallback() {
      this.tryRender();

      this.observer = new MutationObserver(() => this.scheduleRender());
      this.observer.observe(document.body, { childList: true, subtree: true });

      [1000, 3000, 6000].forEach((delay) => {
        window.setTimeout(() => this.tryRender(), delay);
      });

      window.setTimeout(() => this.stopWatching(), 8000);
    }

    disconnectedCallback() {
      this.stopWatching();
      if (this.renderTimer) window.clearTimeout(this.renderTimer);
    }

    scheduleRender() {
      if (this.renderTimer) return;

      this.renderTimer = window.setTimeout(() => {
        this.renderTimer = null;
        this.tryRender();
      }, 250);
    }

    tryRender() {
      if (this.rendered) return;

      const reviews = this.getReviews();
      this.dataset.reviewCandidates = String(reviews.length);

      if (reviews.length < this.maxReviews) {
        this.dataset.reviewStatus = 'waiting-for-two-five-star-reviews';
        return;
      }

      this.innerHTML = reviews
        .slice(0, this.maxReviews)
        .map((review) => `
          <figure class="product-review-excerpts__item">
            <blockquote class="product-review-excerpts__quote">"${this.escapeHtml(this.excerpt(review.text))}"</blockquote>
            ${review.author ? `<figcaption class="product-review-excerpts__author">- ${this.escapeHtml(review.author)}</figcaption>` : ''}
          </figure>
        `)
        .join('');

      this.hidden = false;
      this.rendered = true;
      this.dataset.reviewStatus = 'rendered';
      this.stopWatching();
    }

    getReviews() {
      const reviews = [
        ...this.getJudgeMeReviews(document),
        ...this.getJudgeMeRevampReviews(document),
        ...this.getShopifyReviews(document)
      ];

      return reviews
        .filter((review, index) => (
          reviews.findIndex((candidate) => candidate.text === review.text) === index
        ))
        .filter((review) => review.text.length >= this.minExcerptLength)
        .sort((first, second) => first.text.length - second.text.length);
    }

    getJudgeMeReviews(root) {
      return Array.from(root.querySelectorAll('.jdgm-rev'))
        .filter((review) => {
          const rating = review.querySelector('.jdgm-rev__rating, [data-score], [data-rating]');
          return this.getRatingScore(rating) === 5;
        })
        .map((review) => ({
          text: this.cleanText(
            review.querySelector('.jdgm-rev__body, .jdgm-rev__content, [data-review-body]')?.textContent
          ),
          author: this.cleanText(review.querySelector('.jdgm-rev__author')?.textContent)
        }));
    }

    getJudgeMeRevampReviews(root) {
      return Array.from(root.querySelectorAll('.jm-review-item'))
        .filter((review) => {
          const rating = review.querySelector('.jm-star-rating[aria-label]');
          return this.getRatingScore(rating) === 5;
        })
        .map((review) => ({
          text: this.cleanText(
            review.querySelector(
              '.jdgm-review-content__body-content, .jm-review-content__body'
            )?.textContent
          ),
          author: this.cleanText(review.querySelector('.jm-reviewer-info__name')?.textContent)
        }));
    }

    getShopifyReviews(root) {
      return Array.from(root.querySelectorAll('.spr-review'))
        .filter((review) => {
          const stars = review.querySelectorAll('.spr-icon-star, .spr-icon-star-full').length;
          return stars === 5;
        })
        .map((review) => ({
          text: this.cleanText(review.querySelector('.spr-review-content-body')?.textContent),
          author: this.cleanText(review.querySelector('.spr-review-header-byline strong')?.textContent)
        }));
    }

    cleanText(text = '') {
      return text.replace(/\s+/g, ' ').trim();
    }

    getRatingScore(rating) {
      if (!rating) return 0;

      const score = rating.dataset.score || rating.dataset.rating;
      if (score) return Number(score);

      const label = rating.getAttribute('aria-label') || rating.getAttribute('title') || '';
      return Number(label.match(/\d+(?:[.,]\d+)?/)?.[0]?.replace(',', '.')) || 0;
    }

    excerpt(text) {
      if (text.length <= this.maxExcerptLength) return text;

      const sentenceEnd = text.slice(0, this.maxExcerptLength).search(/[.!?](?=\s|$)(?!.*[.!?])/);
      if (sentenceEnd > 60) return text.slice(0, sentenceEnd + 1);

      return `${text.slice(0, this.maxExcerptLength).trim().replace(/[,.!?;:]*$/, '')}...`;
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    stopWatching() {
      if (!this.observer) return;
      this.observer.disconnect();
      this.observer = null;
    }
  }

  customElements.define('product-review-excerpts', ProductReviewExcerpts);
}
